class CartPickupData {
  constructor() {
    this.init();
  }

  /**
   * Lee las traducciones inyectadas desde Liquid (locale keys) en
   * location-selector-popup.liquid. Si el script no está presente,
   * devuelve un objeto vacío y cada caller usa su propio texto en
   * español como fallback.
   * @returns {object}
   */
  getTranslations() {
    if (this._translations) return this._translations;
    try {
      const script = document.getElementById('location-selector-translations');
      this._translations = script ? JSON.parse(script.textContent) : {};
    } catch (error) {
      this._translations = {};
    }
    return this._translations;
  }

  init() {
    // Debug: Log cart attributes al cargar
    this.debugCartAttributes('🔄 [CART DEBUG] Inicializando - Cargando cart attributes...');
    
    this.loadPickupData();
    this.setupLocationSelectorListener();
    // Sincronizar datos de localStorage al carrito si es necesario (antes de validar)
    this.syncLocalStorageToCart();
    this.validatePickupData();
    this.setupCheckoutValidation();
    this.setupCartDrawerDutyFreeGate();
    this.setupQuantityStepperDutyFreeGuard();
    this.syncLocationAttributes();
  }

  /**
   * Valida los límites duty-free contra el cart drawer (independiente de la
   * página actual, a diferencia de setupCheckoutValidation que solo corre en
   * /cart) y bloquea la navegación del botón "Ir al carrito" del drawer si hay
   * exceso. Se usa delegación de eventos porque el drawer reemplaza su propio
   * HTML (incluido el botón) en cada cambio de cantidad.
   */
  setupCartDrawerDutyFreeGate() {
    // Evaluación inicial (por si el drawer ya tiene items al cargar la página)
    this.validateDutyFreeOnCartPageAsync();

    // Re-evaluar en cada cambio del carrito (agregar, +/-, eliminar), sin importar
    // la página actual — a diferencia de la suscripción de setupCheckoutValidation,
    // que solo se registra en /cart.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.validateDutyFreeOnCartPageAsync();
      });
    }

    document.addEventListener('click', (event) => {
      const button = event.target.closest('#CartDrawer-ViewCartButton');
      if (!button) return;
      if (button.getAttribute('data-duty-free-blocked') === 'true') {
        event.preventDefault();
        event.stopPropagation();
        const alertEl = document.getElementById('cart-drawer-duty-free-alert');
        if (alertEl) alertEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  /**
   * Bloqueo PREVENTIVO (no reactivo) del stepper +/- de líneas sueltas de
   * licores en el carrito/drawer: antes de que el clic en "+" llegue a
   * disparar /cart/change.js, calcula si el nuevo total superaría el límite
   * de litros y, si es así, revierte el valor y cancela el evento ahí
   * mismo — la petición al servidor nunca se envía.
   *
   * Solo aplica a líneas SUELTAS: las líneas de bundle no tienen quantity-input
   * propio en este theme (solo botón de eliminar, ver main-cart-items.liquid /
   * cart-drawer.liquid), así que no hay stepper de bundle que interceptar.
   *
   * Se implementa con un listener en fase de CAPTURA sobre `document`, sin
   * tocar assets/global.js (QuantityInput, compartido por todo el sitio) ni
   * assets/cart.js: la fase de captura siempre corre antes que los listeners
   * en fase de burbuja registrados sobre elementos descendientes (como el
   * listener de <cart-items>/<cart-drawer-items> que dispara la petición), y
   * stopPropagation() ahí detiene el evento por completo — nunca llega a
   * disparar la petición.
   */
  setupQuantityStepperDutyFreeGuard() {
    document.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!input || !input.matches || !input.matches('.quantity__input[data-quantity-variant-id]')) return;
        // Solo dentro de un contexto real de carrito (página de carrito o drawer)
        if (!input.closest('cart-items, cart-drawer-items')) return;

        const state = this.dutyFreeState;
        if (!state) return; // aún no calculado (muy poco probable) — no bloquear, falla abierto

        const newQty = parseInt(input.value, 10);
        const prevQty = parseInt(input.getAttribute('value'), 10) || 0;
        if (!Number.isFinite(newQty) || newQty <= prevQty) return; // solo interesa incrementar

        const deltaQty = newQty - prevQty;

        // Categoría/volumen vienen renderizados en el propio input (data-duty-free-*,
        // ver main-cart-items.liquid y cart-drawer.liquid) — no en el data-island
        // global cart-variant-metafields, que solo se renderiza en el load inicial
        // de la página y falta si la línea se agregó después vía AJAX.
        const category = input.dataset.dutyFreeCategory || '';
        if (category !== 'licores') return; // por ahora solo licores tiene límite por litros configurado

        // Licores se valida solo por litros; ítems sin volumen identificable
        // (mlPerUnit == 0) no se bloquean (decisión explícita 2026-08-17: se
        // quitó el límite/conteo por unidades).
        const mlPerUnit = parseInt(input.dataset.dutyFreeMl, 10) || 0;
        if (mlPerUnit <= 0) return;

        let exceeds = false;
        let message = '';

        const prospectiveMl = state.mlUsed + mlPerUnit * deltaQty;
        if (state.mlLimit > 0 && prospectiveMl > state.mlLimit) {
          exceeds = true;
          const fmtL = (ml) => (ml / 1000).toLocaleString('es', { maximumFractionDigits: 2 });
          message = (
            this.getTranslations().dutyFreeLitersStepperBlocked ||
            'No puedes agregar más: superarías el límite de licores para tu [flight] ([limit] litros).'
          )
            .replace('[flight]', state.ftLabel)
            .replace('[limit]', fmtL(state.mlLimit));
        }

        if (!exceeds) return;

        // Detiene el evento por completo: el listener de <cart-items>/<cart-drawer-items>
        // (fase de burbuja) nunca lo recibe, así que /cart/change.js nunca se llama.
        event.stopPropagation();
        input.value = input.getAttribute('value');

        const alertHtml =
          '<div class="duty-free-limits__alert duty-free-limits__alert--error"><p class="duty-free-limits__message">' +
          message +
          '</p></div>';

        if (input.closest('cart-drawer-items')) {
          // Contenedor dedicado del drawer: no comparte espacio con nada más.
          const drawerAlertEl = document.getElementById('cart-drawer-duty-free-alert');
          if (drawerAlertEl) {
            drawerAlertEl.innerHTML = alertHtml;
            drawerAlertEl.style.display = '';
            // Un frame de margen: recién se pasó de display:none a visible,
            // y scrollIntoView() en el mismo tick puede usar geometría vieja
            // (el navegador todavía no hizo layout del elemento ya visible).
            requestAnimationFrame(() => {
              drawerAlertEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
          }
        } else {
          // #duty-free-limits en cart page también contiene el script
          // #duty-free-limits-config — sobrescribir su innerHTML directo lo
          // destruiría. Se usa/crea el mismo sub-wrapper que ya usa
          // validateDutyFreeOnCartPageAsync().
          const cartPageContainer = document.getElementById('duty-free-limits');
          if (cartPageContainer) {
            const validationEl =
              cartPageContainer.querySelector('.duty-free-limits__cart-validation') || cartPageContainer;
            // El bloque está oculto por CSS por defecto (mismo mecanismo que usa
            // el render Liquid). NO se toca data-duty-free-has-excess aquí: ese
            // atributo también lo lee hasDutyFreeExcess() para deshabilitar el
            // botón de checkout, y este bloqueo preventivo impidió el cambio —
            // el carrito real sigue dentro del límite, así que el checkout debe
            // seguir habilitado. Solo se fuerza visible el aviso puntual.
            validationEl.style.display = 'block';
            let alertsWrap = cartPageContainer.querySelector('.duty-free-limits__alerts');
            if (!alertsWrap) {
              alertsWrap = document.createElement('div');
              alertsWrap.className = 'duty-free-limits__alerts';
              alertsWrap.setAttribute('role', 'alert');
              validationEl.appendChild(alertsWrap);
            }
            alertsWrap.innerHTML = alertHtml;
            // Mismo motivo que en el drawer: esperar un frame a que el
            // navegador haga layout tras el display:block recién puesto.
            requestAnimationFrame(() => {
              validationEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
          }
        }
      },
      true // fase de captura
    );
  }

  /**
   * Función helper para loggear cart attributes de forma consistente
   * @param {string} context - Contexto del log (ej: "Al cargar", "Después de actualizar")
   */
  async debugCartAttributes(context) {
    try {
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        const timestamp = new Date().toISOString();
        
        console.group(`📦 ${context}`);
        console.log('⏰ Timestamp:', timestamp);
        console.log('🛒 Cart ID:', cart.token || 'N/A');
        console.log('📊 Total de atributos:', cart.attributes ? Object.keys(cart.attributes).length : 0);
        
        if (cart.attributes && Object.keys(cart.attributes).length > 0) {
          console.log('📋 Todos los cart attributes:');
          console.table(cart.attributes);
          
          // Resaltar atributos de pickup
          const pickupAttributes = {};
          Object.keys(cart.attributes).forEach(key => {
            if (key.startsWith('pickup_') || key === 'branch_filter_selection') {
              pickupAttributes[key] = cart.attributes[key];
            }
          });
          
          if (Object.keys(pickupAttributes).length > 0) {
            console.log('🎯 Atributos de pickup:');
            console.table(pickupAttributes);
          } else {
            console.warn('⚠️ No se encontraron atributos de pickup');
          }
        } else {
          console.warn('⚠️ El carrito no tiene atributos');
        }
        console.groupEnd();
      } else {
        console.error('❌ Error al obtener cart attributes:', cartResponse.statusText);
      }
    } catch (error) {
      console.error('❌ Error al debuggear cart attributes:', error);
    }
  }

  async loadPickupData() {
    try {
      // Debug: Log antes de cargar
      await this.debugCartAttributes('🔄 [CART DEBUG] loadPickupData - Antes de cargar datos');
      
      // Intentar cargar desde localStorage primero (más rápido)
      const stored = localStorage.getItem('locationData');
      let locationData = stored ? JSON.parse(stored) : null;

      // Si no hay datos en localStorage, intentar cargar desde cart attributes
      if (!locationData) {
        try {
          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            if (cart.attributes && Object.keys(cart.attributes).length > 0) {
              // Debug: Log cuando se cargan desde cart attributes
              console.log('📥 [CART DEBUG] Cargando datos desde cart attributes');
              // Mapear cart attributes a locationData
              locationData = {
                country: cart.attributes.pickup_country || '',
                airport: cart.attributes.pickup_airport || '',
                terminal: cart.attributes.pickup_terminal_raw || cart.attributes.pickup_terminal || '',
                department: cart.attributes.pickup_department || '',
                date: cart.attributes.pickup_date || '',
                time: cart.attributes.pickup_time || '',
                dateDisplay: cart.attributes.pickup_date_display || cart.attributes.pickup_date || '',
                timeDisplay: cart.attributes.pickup_time_display || cart.attributes.pickup_time || '',
                location_id: cart.attributes.pickup_location_id || '',
                location_name: cart.attributes.pickup_location_name || '',
                timestamp: cart.attributes.pickup_timestamp || '',
              };
            }
          }
        } catch (error) {
          console.warn('No se pudieron cargar datos desde cart attributes:', error);
        }
      }

      if (locationData) {
        // Buscar elementos del carrito específicamente (no del popup)
        // Los elementos del carrito están dentro de .cart__pickup-info
        const cartPickupInfo = document.querySelector('.cart__pickup-info');

        const countryEl = cartPickupInfo?.querySelector('#pickup-country') || document.getElementById('pickup-country');
        const airportEl = cartPickupInfo?.querySelector('#pickup-airport') || document.getElementById('pickup-airport');
        const terminalEl =
          cartPickupInfo?.querySelector('#pickup-terminal') || document.getElementById('pickup-terminal');
        const dateEl = cartPickupInfo?.querySelector('#pickup-date') || document.getElementById('pickup-date');
        const timeEl = cartPickupInfo?.querySelector('#pickup-time') || document.getElementById('pickup-time');

        if (countryEl && countryEl.closest('.cart__pickup-info')) {
          const countryRaw = this.getCountryDisplayText(locationData.country);
          countryEl.textContent = countryRaw ? countryRaw.charAt(0).toUpperCase() + countryRaw.slice(1) : '-';
        }
        if (airportEl && airportEl.closest('.cart__pickup-info')) {
          airportEl.textContent = locationData.airport ? this.getAirportDisplayText() : '-';
        }
        if (terminalEl && terminalEl.closest('.cart__pickup-info')) {
          // Convertir terminal-1 a Terminal 1
          const terminalText = this.getTerminalText(locationData.terminal);
          terminalEl.textContent = terminalText || '-';
        }
        if (dateEl && dateEl.closest('.cart__pickup-info')) {
          dateEl.textContent =
            this.formatDateForLocale(locationData.date) || locationData.dateDisplay || locationData.date || '-';
        }
        if (timeEl && timeEl.closest('.cart__pickup-info')) {
          timeEl.textContent = locationData.timeDisplay || locationData.time || '-';
        }
      }
    } catch (error) {
      console.error('Error al cargar datos de recogida:', error);
    }
  }

  /**
   * Obtiene la hora actual en el huso horario de Panamá (UTC-5),
   * normalizando la hora del navegador a la zona horaria de la tienda.
   */
  getElSalvadorNow() {
    const now = new Date();
    const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
    const elSalvadorOffsetMinutes = -6 * 60; // UTC-6
    return new Date(utcTime + elSalvadorOffsetMinutes * 60000);
  }

  /**
   * Valores para los atributos del carrito (pickup_terminal, pickup_airport).
   * Siempre en español, sin importar el idioma de la tienda: checkout, órdenes
   * e integraciones leen estos textos. Las traducciones son solo para mostrar.
   */
  getTerminalAttributeText(terminalValue) {
    if (!terminalValue) return '-';

    const mapping = {
      'terminal-1': 'Tienda de Salida',
      'terminal-2': 'Tienda de Llegada 2',
    };

    return mapping[terminalValue.toLowerCase()] || terminalValue;
  }

  getAirportAttributeText() {
    return 'Aeropuerto Internacional de El Salvador';
  }

  /** Nombre del aeropuerto traducido, solo para mostrar. */
  getAirportDisplayText() {
    return this.getTranslations().popupAirportName || this.getAirportAttributeText();
  }

  /** Nombre del país traducido, solo para mostrar (pickup_country se guarda sin traducir). */
  getCountryDisplayText(country) {
    if (!country) return '';
    const t = this.getTranslations();
    const mapping = { 'el salvador': t.popupCountryElSalvador, panama: t.popupCountryPanama, 'panamá': t.popupCountryPanama };
    return mapping[country.toLowerCase()] || country;
  }

  /**
   * Fecha para mostrar cuando el idioma de la tienda no es español (ej: pt-BR).
   * En español devuelve '' para usar pickup_date_display tal como se guardó.
   * @param {string} dateValue - Fecha en formato YYYY-MM-DD
   * @returns {string}
   */
  formatDateForLocale(dateValue) {
    const lang = document.documentElement.lang || 'es';
    if (!dateValue || lang.startsWith('es')) return '';
    const date = new Date(dateValue + 'T00:00:00');
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
  }

  getTerminalText(terminalValue) {
    if (!terminalValue) return '-';

    const t = this.getTranslations();
    const mapping = {
      'terminal-1': t.popupTerminal1 || 'Tienda de Salida',
      'terminal-2': t.popupTerminal2 || 'Tienda de Llegada 2',
    };

    return mapping[terminalValue.toLowerCase()] || terminalValue;
  }

  setupLocationSelectorListener() {
    // Escuchar cuando se actualiza la información de ubicación
    document.addEventListener('locationDataUpdated', async () => {
      await this.debugCartAttributes('🔄 [CART DEBUG] Evento locationDataUpdated - Datos actualizados');
      this.loadPickupData();
      this.validatePickupData();
      this.syncLocationAttributes(); // Sincronizar después de actualizar
    });

    // También escuchar cambios en localStorage (por si se actualiza desde otra pestaña)
    window.addEventListener('storage', async (e) => {
      if (e.key === 'locationData') {
        await this.debugCartAttributes('🔄 [CART DEBUG] Evento storage - Datos cambiados desde otra pestaña');
        this.loadPickupData();
        this.validatePickupData();
        this.syncLocationAttributes(); // Sincronizar después de actualizar
      }
    });
  }

  /**
   * Valida que existan datos de pickup válidos
   * Si no existen o son inválidos, abre automáticamente el modal de selección
   */
  async validatePickupData() {
    // Solo validar en la página del carrito
    if (!window.location.pathname.includes('/cart')) {
      return;
    }

    try {
      // Obtener datos de pickup desde localStorage o cart attributes
      const stored = localStorage.getItem('locationData');
      let locationData = stored ? JSON.parse(stored) : null;

      // Si no hay datos en localStorage, intentar desde cart attributes
      if (!locationData) {
        try {
          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            if (cart.attributes && Object.keys(cart.attributes).length > 0) {
              locationData = {
                country: cart.attributes.pickup_country || '',
                airport: cart.attributes.pickup_airport || '',
                terminal: cart.attributes.pickup_terminal_raw || cart.attributes.pickup_terminal || '',
                department: cart.attributes.pickup_department || '',
                date: cart.attributes.pickup_date || '',
                time: cart.attributes.pickup_time || '',
                dateDisplay: cart.attributes.pickup_date_display || cart.attributes.pickup_date || '',
                timeDisplay: cart.attributes.pickup_time_display || cart.attributes.pickup_time || '',
                location_id: cart.attributes.pickup_location_id || '',
                location_name: cart.attributes.pickup_location_name || '',
                timestamp: cart.attributes.pickup_timestamp || '',
              };
            }
          }
        } catch (error) {
          console.warn('No se pudieron cargar datos desde cart attributes:', error);
        }
      }

      // Validar que existan los datos mínimos requeridos
      const hasRequiredData =
        locationData &&
        locationData.country &&
        locationData.airport &&
        locationData.terminal &&
        locationData.date &&
        locationData.time &&
        locationData.location_id &&
        locationData.location_name;

      if (!hasRequiredData) {
        // No hay datos válidos
        console.warn('⚠️ [CART DEBUG] validatePickupData - No hay datos válidos en localStorage ni cart attributes');
        
        // Verificar si hay datos en localStorage que se puedan sincronizar
        const stored = localStorage.getItem('locationData');
        if (stored) {
          console.log('📝 [CART DEBUG] validatePickupData - Hay datos en localStorage pero incompletos, intentando sincronizar...');
          // Los datos están incompletos, abrir el modal para que el usuario los complete
          this.openLocationSelector();
        } else {
          // No hay datos en ningún lado, abrir el modal
          this.openLocationSelector();
        }
        return;
      }

      // Validar que la fecha y hora no sean en el pasado
      const isValidDateTime = this.validateDateTime(locationData.date, locationData.time);
      if (!isValidDateTime) {
        // La fecha/hora es inválida, abrir el modal para que el usuario la corrija
        console.warn('⚠️ [CART DEBUG] validatePickupData - Fecha/hora inválida (en el pasado o menos de 1 hora de antelación)');
        this.openLocationSelector();
        return;
      }

      // Si llegamos aquí, los datos son válidos
      console.log('✅ [CART DEBUG] validatePickupData - Datos válidos encontrados');
    } catch (error) {
      console.error('Error al validar datos de pickup:', error);
      // En caso de error, abrir el modal para asegurar que el usuario seleccione
      this.openLocationSelector();
    }
  }

  /**
   * Valida que la fecha y hora no sean en el pasado
   * y que haya al menos 30 minutos de antelación
   * @param {string} dateValue - Fecha en formato YYYY-MM-DD
   * @param {string} timeValue - Hora en formato HH:MM
   * @returns {boolean} - true si es válido, false si no
   */
  validateDateTime(dateValue, timeValue) {
    if (!dateValue || !timeValue) {
      return false;
    }

    try {
      const now = this.getElSalvadorNow();
      const [year, month, day] = dateValue.split('-').map(Number);
      const [hours, minutes] = timeValue.split(':').map(Number);

      // Crear fecha/hora de recogida
      const pickupDateTime = new Date(year, month - 1, day, hours, minutes);

      // Verificar que no sea en el pasado
      if (pickupDateTime <= now) {
        return false;
      }

      // Verificar que haya al menos 30 minutos de antelación
      const thirtyMinutesFromNow = new Date(now.getTime() + 30 * 60 * 1000);
      if (pickupDateTime < thirtyMinutesFromNow) {
        return false;
      }

      // Verificar máximo de 15 días de anticipación
      const maxAllowed = new Date(now);
      maxAllowed.setHours(23, 59, 59, 999);
      maxAllowed.setDate(maxAllowed.getDate() + 15);
      if (pickupDateTime > maxAllowed) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error al validar fecha/hora:', error);
      return false;
    }
  }

  /**
   * Abre el modal de selección de ubicación
   */
  openLocationSelector() {
    // Esperar a que el modal esté disponible
    const checkModal = () => {
      if (window.locationSelectorPopup && typeof window.locationSelectorPopup.open === 'function') {
        window.locationSelectorPopup.open();
      } else {
        // Reintentar después de un breve delay
        setTimeout(checkModal, 100);
      }
    };

    // Esperar un poco para asegurar que el DOM esté listo
    setTimeout(checkModal, 200);
  }

  /**
   * Configura la validación del botón de checkout
   * Impide el checkout si los datos de pickup son inválidos
   */
  setupCheckoutValidation() {
    // Solo en la página del carrito
    if (!window.location.pathname.includes('/cart')) {
      return;
    }

    // Esperar a que el DOM esté listo
    const setupValidation = async () => {
      const checkoutButton = document.getElementById('checkout');
      const checkoutForm = document.getElementById('cart') || document.querySelector('form[action="/cart"]');

      if (!checkoutButton) {
        setTimeout(setupValidation, 100);
        return;
      }

      this.debugCartAttributes('🔄 [CART DEBUG] setupCheckoutValidation - Configurando validación');

      // Validar duty-free en cart page (cuenta ítems + bundles) y marcar exceso/aviso antes de evaluar el botón
      await this.validateDutyFreeOnCartPageAsync();
      await this.updateCheckoutButtonStateAsync(checkoutButton, false);

      // Ejecuta las validaciones de pickup/duty-free/inventario. Si todo es válido,
      // marca _checkoutValidationPassed y re-dispara el click del botón para dejar
      // pasar el submit nativo (preserva el submitter name="checkout").
      //
      // IMPORTANTE: preventDefault() en un handler async llamado DESPUÉS de un
      // await llega tarde — el navegador ya decidió si navega antes de que la
      // promesa resuelva, así que a veces el bloqueo no surte efecto (carrera
      // que se pierde más seguido en redes lentas, como el wifi del aeropuerto).
      // Por eso cada handler abajo llama preventDefault() de forma síncrona,
      // ANTES de cualquier await, y solo deja pasar el submit real una vez
      // resuelta la validación.
      this._runCheckoutValidation = async (checkoutButton, logPrefix) => {
        await this.debugCartAttributes(`🔄 [CART DEBUG] ${logPrefix} - Verificando atributos`);
        const isValid = await this.isPickupDataValidAsync();
        const dutyFreeExcess = this.hasDutyFreeExcess();
        const inventoryExcess = await this.getCartInventoryExcess();
        console.log(`✅ [CART DEBUG] Validación de checkout (${logPrefix}):`, isValid ? 'VÁLIDO' : 'INVÁLIDO', 'Duty-free exceso:', dutyFreeExcess, 'Exceso inventario:', inventoryExcess.excess);

        if (!isValid) {
          this.showCheckoutError();
          this.openLocationSelector();
          return;
        }
        if (dutyFreeExcess) {
          const dutyFreeBlock = document.getElementById('duty-free-excess-block') || document.querySelector('[data-duty-free-has-excess="true"]');
          if (dutyFreeBlock) dutyFreeBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        if (inventoryExcess.excess) {
          this.showCheckoutError(inventoryExcess.message);
          return;
        }

        await this.debugCartAttributes(`🔄 [CART DEBUG] Checkout permitido (${logPrefix}) - Atributos finales antes de checkout`);
        // Validación superada: dejar pasar el próximo click/submit sintético sin re-validar.
        this._checkoutValidationPassed = true;
        checkoutButton.click();
      };

      // Handler de click extraído para poder re-adjuntarlo si el botón se re-renderiza tras una actualización del carrito
      this._checkoutClickHandler = (e) => {
        if (this._checkoutValidationPassed) {
          // Click sintético disparado tras validar: dejar que siga su curso normal
          // (el submit del formulario es quien finalmente resetea la bandera).
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        this._runCheckoutValidation(checkoutButton, 'Click en botón checkout');
      };

      checkoutButton.addEventListener('click', this._checkoutClickHandler);

      // También prevenir el submit del formulario (cubre Enter y cualquier submit no iniciado por click)
      if (checkoutForm) {
        checkoutForm.addEventListener('submit', (e) => {
          const submitTarget = e.submitter;
          if (!submitTarget || submitTarget.getAttribute('name') !== 'checkout') return;

          if (this._checkoutValidationPassed) {
            // Ya validado (por el click handler o por este mismo submit en la
            // vuelta anterior): dejar pasar el submit real y resetear la bandera.
            this._checkoutValidationPassed = false;
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          this._runCheckoutValidation(checkoutButton, 'Submit del formulario');
        });
      }

      // Escuchar cambios en los datos de pickup para actualizar el estado del botón (con spinner mientras valida)
      document.addEventListener('locationDataUpdated', () => {
        this.updateCheckoutButtonStateAsync(checkoutButton, true);
      });

      // Cuando el usuario confirma una nueva sucursal en el popup, revalidar con spinner y luego habilitar/deshabilitar
      window.addEventListener('locationChanged', () => {
        if (document.getElementById('checkout')) {
          this.updateCheckoutButtonStateAsync(checkoutButton, true);
        }
      });

      // Tras actualización del carrito (cantidades, ítems), re-evaluar duty-free y estado del botón (sin spinner).
      // También re-adjunta el click handler porque Dawn puede re-renderizar el botón #checkout al actualizar secciones.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
          await this.validateDutyFreeOnCartPageAsync();
          const btn = document.getElementById('checkout');
          if (btn) {
            await this.updateCheckoutButtonStateAsync(btn, false);
            if (this._checkoutClickHandler) {
              btn.removeEventListener('click', this._checkoutClickHandler);
              btn.addEventListener('click', this._checkoutClickHandler);
            }
          }
        });
      }

      // Validar periódicamente (por si cambian los datos desde otra pestaña), sin spinner
      setInterval(() => {
        const btn = document.getElementById('checkout');
        if (btn) this.updateCheckoutButtonStateAsync(btn, false);
      }, 5000); // Cada 5 segundos
    };

    // Iniciar después de un breve delay para asegurar que el DOM esté listo
    setTimeout(setupValidation, 300);
  }

  /**
   * Verifica si los datos de pickup son válidos
   * @returns {boolean} - true si son válidos, false si no
   */
  isPickupDataValid() {
    try {
      // Obtener datos de pickup desde localStorage primero (más rápido)
      const stored = localStorage.getItem('locationData');
      let locationData = stored ? JSON.parse(stored) : null;

      // Si no hay datos en localStorage, verificar desde cart attributes (síncrono usando cache)
      if (!locationData) {
        // Intentar obtener desde el cache del carrito si está disponible
        // Nota: Esto es una verificación rápida, pero puede no estar actualizado
        // La validación completa se hace en validatePickupData que es asíncrona
        return false; // Si no hay datos en localStorage, asumir inválido por ahora
      }

      // Validar que existan los datos mínimos requeridos
      const hasRequiredData =
        locationData &&
        locationData.country &&
        locationData.airport &&
        locationData.terminal &&
        locationData.date &&
        locationData.time &&
        locationData.location_id &&
        locationData.location_name;

      if (!hasRequiredData) {
        return false;
      }

      // Validar que la fecha y hora no sean en el pasado
      return this.validateDateTime(locationData.date, locationData.time);
    } catch (error) {
      console.error('Error al validar datos de pickup:', error);
      return false;
    }
  }

  /**
   * Verifica de forma asíncrona si los datos de pickup son válidos (incluyendo cart attributes)
   * @returns {Promise<boolean>} - true si son válidos, false si no
   */
  async isPickupDataValidAsync() {
    try {
      // Obtener datos de pickup desde localStorage primero
      const stored = localStorage.getItem('locationData');
      let locationData = stored ? JSON.parse(stored) : null;

      // Si no hay datos en localStorage, intentar desde cart attributes
      if (!locationData) {
        try {
          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            if (cart.attributes && Object.keys(cart.attributes).length > 0) {
              locationData = {
                country: cart.attributes.pickup_country || '',
                airport: cart.attributes.pickup_airport || '',
                terminal: cart.attributes.pickup_terminal_raw || cart.attributes.pickup_terminal || '',
                department: cart.attributes.pickup_department || '',
                date: cart.attributes.pickup_date || '',
                time: cart.attributes.pickup_time || '',
                dateDisplay: cart.attributes.pickup_date_display || cart.attributes.pickup_date || '',
                timeDisplay: cart.attributes.pickup_time_display || cart.attributes.pickup_time || '',
                location_id: cart.attributes.pickup_location_id || '',
                location_name: cart.attributes.pickup_location_name || '',
                timestamp: cart.attributes.pickup_timestamp || '',
              };
            }
          }
        } catch (error) {
          console.warn('No se pudieron cargar datos desde cart attributes:', error);
        }
      }

      // Validar que existan los datos mínimos requeridos
      const hasRequiredData =
        locationData &&
        locationData.country &&
        locationData.airport &&
        locationData.terminal &&
        locationData.date &&
        locationData.time &&
        locationData.location_id &&
        locationData.location_name;

      if (!hasRequiredData) {
        return false;
      }

      // Validar que la fecha y hora no sean en el pasado
      return this.validateDateTime(locationData.date, locationData.time);
    } catch (error) {
      console.error('Error al validar datos de pickup:', error);
      return false;
    }
  }

  /**
   * Indica si hay exceso en límites duty-free (licores/fragancias).
   * La section duty-free-limits renderiza data-duty-free-has-excess="true" cuando aplica.
   * También se puede setear desde validateDutyFreeOnCartPageAsync cuando el conteo en JS detecta exceso.
   * @returns {boolean}
   */
  hasDutyFreeExcess() {
    const el = document.querySelector('[data-duty-free-has-excess="true"]');
    return Boolean(el);
  }

  /**
   * Clasifica product type en categoría duty-free (licores/fragancias). Misma lógica que el theme en product-form.
   * @param {string} productType
   * @returns {string|null} 'licores' | 'fragancias' | null
   */
  productTypeToDutyFreeCategory(productType) {
    const pt = (productType || '').toLowerCase().trim();
    if (!pt) return null;
    if (pt === 'licor' || pt === 'licores' || pt.includes('licor')) return 'licores';
    if (pt === 'fragancia' || pt === 'fragancias' || pt.includes('fragancia')) return 'fragancias';
    return null;
  }

  /**
   * Extrae el volumen en ml de un texto de variante (ej: "750 ml", "1000 ml / Rojo").
   * @returns {number} ml, o 0 si no se puede determinar
   */
  parseDutyFreeMl(str) {
    if (!str) return 0;
    const m = String(str).match(/(\d+(?:[.,]\d+)?)\s*ml\b/i);
    if (m) return Math.round(parseFloat(m[1].replace(',', '.'))) || 0;
    return 0;
  }

  /**
   * Volumen en ml de una variante del carrito: primero el script pre-cargado
   * cart-variant-metafields (metafield custom.volumen_ml o parseado en Liquid),
   * después el título de la variante como fallback.
   */
  getVariantVolumeMl(variantId, fallbackTitle) {
    try {
      const el = document.getElementById('cart-variant-metafields');
      if (el) {
        const map = JSON.parse(el.textContent);
        const ml = map[variantId] && map[variantId].volumen_ml;
        if (ml != null && ml > 0) return parseInt(ml, 10) || 0;
      }
    } catch (_) {}
    return this.parseDutyFreeMl(fallbackTitle);
  }

  /**
   * En la cart page: obtiene carrito + bundles, cuenta licores/fragancias (incl. hijos de bundles)
   * y si hay exceso marca data-duty-free-has-excess="true" y muestra el aviso si no está.
   * Así la validación incluye bundles aunque Liquid no los cuente (ej. sin productType en _bundle_children_info).
   */
  async validateDutyFreeOnCartPageAsync() {
    // El contenedor #duty-free-limits existe en CUALQUIER template que tenga la
    // sección (cart Y product), pero su alerta Liquid interna solo se renderiza
    // en cart page (request.page_type == 'cart'). Restringimos ese target al
    // mismo alcance para no mostrar el banner completo en la página de producto.
    // El drawer, en cambio, corre sin importar la página (ese es el fix).
    const cartPageContainer = window.location.pathname.includes('/cart')
      ? document.getElementById('duty-free-limits')
      : null;
    const drawerAlertContainer = document.getElementById('cart-drawer-duty-free-alert');
    const drawerViewCartButton = document.getElementById('CartDrawer-ViewCartButton');
    if (!cartPageContainer && !drawerAlertContainer) return;

    // Default = límites de negocio actuales (5L tanto para salida como para
    // llegada, sin límite de fragancias). Se usa cuando la página actual no
    // renderiza la sección duty-free-limits (ej. drawer abierto en
    // homepage/colección) y por lo tanto no hay #duty-free-limits-config en el DOM.
    let config = {
      licores: {
        salida: { litros_ml: 5000 },
        llegada: { litros_ml: 5000 },
      },
      fragancias: 0,
    };
    const configEl = document.getElementById('duty-free-limits-config');
    if (configEl) {
      try {
        config = JSON.parse(configEl.textContent);
      } catch (_) {}
    }

    try {
      const [cartRes, sectionsRes] = await Promise.all([
        fetch('/cart.js'),
        fetch('/cart?sections=cart-bundle-data')
      ]);
      const cart = await cartRes.json();
      const sectionsData = await sectionsRes.json();
      const sections = sectionsData.sections || {};
      const sectionKey = sections['cart-bundle-data'] ? 'cart-bundle-data' : Object.keys(sections).find((k) => k.includes('cart-bundle'));
      const sectionHtml = sectionKey ? sections[sectionKey] : null;
      let bundles = [];
      if (sectionHtml) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        const script = doc.querySelector('script[data-bundle-data]');
        if (script) {
          try {
            const data = JSON.parse(script.textContent);
            bundles = data.bundles || [];
          } catch (_) {}
        }
      }

      const counts = { licores: 0, fragancias: 0 };
      // Licores se mide solo en ml; ítems sin volumen identificable no se validan.
      const licoresVol = { ml: 0 };
      const addLicores = (qty, mlPerUnit) => {
        if (mlPerUnit > 0) licoresVol.ml += mlPerUnit * qty;
      };
      const matchBundle = (b, item) => String(b.parentItemId) === String(item.id) || Number(b.parentItemId) === Number(item.id);

      for (const item of cart.items || []) {
        const bundle = bundles.find((b) => matchBundle(b, item));
        if (bundle && bundle.children && bundle.children.length) {
          for (const child of bundle.children) {
            const cat = this.productTypeToDutyFreeCategory(child.productType);
            const qty = (child.quantity || 1) * (item.quantity || 1);
            if (cat) counts[cat] += qty;
            if (cat === 'licores') addLicores(qty, this.parseDutyFreeMl(child.variantTitle || child.title || ''));
          }
        } else if (item.properties && item.properties._bundle_children) {
          try {
            const bundleChildren = JSON.parse(item.properties._bundle_children);
            const childrenInfo = (item.properties._bundle_children_info && JSON.parse(item.properties._bundle_children_info)) || [];
            const qtyMultiplier = item.quantity || 1;
            const childrenArr = Array.isArray(bundleChildren) ? bundleChildren : [];
            childrenArr.forEach((child, index) => {
              const qty = (child.quantity || 1) * qtyMultiplier;
              let info = Array.isArray(childrenInfo) ? childrenInfo.find((i) => String(i.cartLineId) === String(child.cartLineId)) : null;
              if (!info && Array.isArray(childrenInfo) && index < childrenInfo.length) info = childrenInfo[index];
              const productType = info && (info.productType || info.product_type);
              const cat = this.productTypeToDutyFreeCategory(productType);
              if (cat) counts[cat] += qty;
              // Hijos de bundle por properties: ml desde variantTitle del _bundle_children_info
              if (cat === 'licores') addLicores(qty, this.parseDutyFreeMl(info && (info.variantTitle || info.variant_title)));
            });
          } catch (_) {
            const cat = this.productTypeToDutyFreeCategory(item.product_type);
            const qty = item.quantity || 1;
            if (cat) counts[cat] += qty;
            if (cat === 'licores') addLicores(qty, this.getVariantVolumeMl(item.variant_id, item.variant_title || ''));
          }
        } else {
          const cat = this.productTypeToDutyFreeCategory(item.product_type);
          const qty = item.quantity || 1;
          if (cat) counts[cat] += qty;
          if (cat === 'licores') addLicores(qty, this.getVariantVolumeMl(item.variant_id, item.variant_title || ''));
        }
      }

      const limitFragancias = config.fragancias != null ? config.fragancias : 9;
      // 0 (o negativo) = sin límite para esa categoría
      const excessFragancias = limitFragancias > 0 && counts.fragancias > limitFragancias;

      // Licores: límite en litros por tipo de vuelo, o legacy numérico
      const t = this.getTranslations();
      const licoresMessages = [];
      const fmtL = (ml) => (ml / 1000).toLocaleString('es', { maximumFractionDigits: 2 });
      // Hoisted para poder guardarlos en this.dutyFreeState (usado por el
      // guard preventivo del stepper +/-, ver setupQuantityStepperDutyFreeGuard)
      let dutyFreeFt = 'llegada';
      let dutyFreeFtLabel = t.dutyFreeFlightArriving || 'vuelo de llegada';
      let dutyFreeLimMl = 0;
      if (config.licores && typeof config.licores === 'object') {
        const ft = (cart.attributes && cart.attributes.pickup_flight_type) === 'salida' ? 'salida' : 'llegada';
        const lim = config.licores[ft] || config.licores.llegada || {};
        const limMl = parseInt(lim.litros_ml, 10) || 0;
        // Solo texto para mensajes; la lógica usa ft
        const ftLabel =
          ft === 'salida' ? t.dutyFreeFlightDeparting || 'vuelo de salida' : t.dutyFreeFlightArriving || 'vuelo de llegada';
        dutyFreeFt = ft;
        dutyFreeFtLabel = ftLabel;
        dutyFreeLimMl = limMl;
        if (limMl > 0 && licoresVol.ml > limMl) {
          licoresMessages.push(
            (
              t.dutyFreeLitersExceeded ||
              'Tienes <strong>[count] litros</strong> de licores en tu carrito. El máximo permitido para tu [flight] es <strong>[limit] litros</strong>. Reduce la cantidad o elimina algún producto para continuar.'
            )
              .replace('[count]', fmtL(licoresVol.ml))
              .replace('[flight]', ftLabel)
              .replace('[limit]', fmtL(limMl))
          );
        }
      } else {
        const limitLicores = config.licores != null ? config.licores : 6;
        if (limitLicores > 0 && counts.licores > limitLicores) {
          licoresMessages.push(
            (
              t.dutyFreeUnitsExceeded ||
              'Tienes <strong>[count]</strong> unidades de [category]. El máximo permitido es <strong>[limit]</strong>. Reduce la cantidad o elimina algún producto para continuar.'
            )
              .replace('[count]', counts.licores)
              .replace('[category]', (t.dutyFreeCategories && t.dutyFreeCategories.licores) || 'licores')
              .replace('[limit]', limitLicores)
          );
        }
      }
      const excessLicores = licoresMessages.length > 0;
      const hasExcess = excessLicores || excessFragancias;

      const alertHtml = () => {
        let html = '';
        licoresMessages.forEach((msg) => {
          html += '<div class="duty-free-limits__alert duty-free-limits__alert--error"><p class="duty-free-limits__message">' + msg + '</p><p class="duty-free-limits__solution caption">' +
            (t.dutyFreeSolutionLiquor || 'Productos en tu carrito que cuentan como licores: revisa los ítems de tipo Licor o los bundles que incluyen licores y reduce cantidades.') +
            '</p></div>';
        });
        if (excessFragancias) {
          html += '<div class="duty-free-limits__alert duty-free-limits__alert--error"><p class="duty-free-limits__message">' +
            (t.dutyFreeUnitsExceeded || 'Tienes <strong>[count]</strong> unidades de [category]. El máximo permitido es <strong>[limit]</strong>. Reduce la cantidad o elimina algún producto para continuar.')
              .replace('[count]', counts.fragancias)
              .replace('[category]', (t.dutyFreeCategories && t.dutyFreeCategories.fragancias) || 'fragancias')
              .replace('[limit]', limitFragancias) +
            '</p><p class="duty-free-limits__solution caption">' +
            (t.dutyFreeSolutionFragrances || 'Productos en tu carrito que cuentan como fragancias: revisa los ítems de tipo Fragancia o los bundles que incluyen fragancias y reduce cantidades.') +
            '</p></div>';
        }
        return html;
      };

      // Cart page: sección duty-free-limits (misma estructura que el render Liquid)
      if (cartPageContainer && hasExcess) {
        const validationEl = cartPageContainer.querySelector('.duty-free-limits__cart-validation') || cartPageContainer;
        validationEl.setAttribute('data-duty-free-has-excess', 'true');
        if (!validationEl.id) validationEl.id = 'duty-free-excess-block';
        let alertsWrap = cartPageContainer.querySelector('.duty-free-limits__alerts');
        if (!alertsWrap) {
          alertsWrap = document.createElement('div');
          alertsWrap.className = 'duty-free-limits__alerts';
          alertsWrap.setAttribute('role', 'alert');
          validationEl.appendChild(alertsWrap);
        }
        if (alertsWrap.querySelectorAll('.duty-free-limits__alert').length === 0) {
          alertsWrap.innerHTML = alertHtml();
        }
      }

      // Cart drawer: alerta propia + bloqueo del botón "Ir al carrito" (no navega
      // a checkout, pero la excedencia real la garantiza igual la función de
      // Cart & Checkout Validation en el servidor)
      if (drawerAlertContainer) {
        if (hasExcess) {
          drawerAlertContainer.innerHTML = alertHtml();
          drawerAlertContainer.style.display = '';
        } else {
          drawerAlertContainer.innerHTML = '';
          drawerAlertContainer.style.display = 'none';
        }
      }
      if (drawerViewCartButton) {
        if (hasExcess) {
          drawerViewCartButton.setAttribute('data-duty-free-blocked', 'true');
        } else {
          drawerViewCartButton.removeAttribute('data-duty-free-blocked');
        }
      }

      // Estado consultable de forma síncrona por setupQuantityStepperDutyFreeGuard()
      // para bloquear el stepper +/- ANTES de que llegue a /cart/change.js.
      this.dutyFreeState = {
        ft: dutyFreeFt,
        ftLabel: dutyFreeFtLabel,
        mlUsed: licoresVol.ml,
        mlLimit: dutyFreeLimMl,
      };
    } catch (e) {
      console.warn('[Duty-free] Error validando en cart page:', e);
    }
  }

  /**
   * Construye un mapa variantId -> total unidades en carrito (solo productos sueltos + hijos de bundles).
   * No incluye el padre del bundle (suele tener "no seguir inventario"); solo se valida inventario de hijos y sueltos.
   * Misma lógica que en product-form para validar inventario por sucursal.
   */
  buildVariantQuantitiesFromCart(cart) {
    const map = {};
    const toId = (v) => {
      if (v == null) return null;
      const s = String(v);
      const gidMatch = s.match(/\/ProductVariant\/(\d+)/);
      return gidMatch ? parseInt(gidMatch[1], 10) : parseInt(s, 10);
    };
    (cart.items || []).forEach((item) => {
      const isBundleParent = item.properties && item.properties._bundle_children && item.properties._bundle_children_info;
      if (!isBundleParent) {
        const lineVariantId = toId(item.variant_id);
        if (lineVariantId) map[lineVariantId] = (map[lineVariantId] || 0) + (item.quantity || 1);
      }
      if (isBundleParent) {
        try {
          const children = JSON.parse(item.properties._bundle_children);
          const info = JSON.parse(item.properties._bundle_children_info);
          const qtyMultiplier = item.quantity || 1;
          (Array.isArray(children) ? children : []).forEach((child, index) => {
            const qty = (child.quantity || 1) * qtyMultiplier;
            let variantId = null;
            const inf = Array.isArray(info) ? (info.find((i) => String(i.cartLineId) === String(child.cartLineId)) || info[index]) : null;
            if (inf) variantId = toId(inf.variantId || inf.variant_id);
            if (variantId) map[variantId] = (map[variantId] || 0) + qty;
          });
        } catch (_) {}
      }
    });
    return map;
  }

  /**
   * Valida que las cantidades del carrito no superen el inventario en la sucursal seleccionada.
   * Si el proxy falla o no hay locationId, no bloquea (allowed).
   * @returns {Promise<{ excess: boolean, message?: string }>}
   */
  async getCartInventoryExcess() {
    try {
      const cartRes = await fetch('/cart.js');
      if (!cartRes.ok) return { excess: false };
      const cart = await cartRes.json();
      const locationId = (cart.attributes && cart.attributes.pickup_location_id) || '';
      if (!locationId || !cart.items || cart.items.length === 0) return { excess: false };

      const variantQuantities = this.buildVariantQuantitiesFromCart(cart);
      const variantIds = Object.keys(variantQuantities).map((k) => parseInt(k, 10)).filter(Boolean);
      if (variantIds.length === 0) return { excess: false };

      const proxyUrl = `/apps/inventory?variantIds=${variantIds.join(',')}&locationId=${encodeURIComponent(locationId)}`;
      const invRes = await fetch(proxyUrl);
      let data = null;
      const text = await invRes.text();
      if (invRes.ok && text) {
        try {
          data = JSON.parse(text);
        } catch (_) {}
      }
      if (!invRes.ok || !data || !data.inventory) return { excess: false };

      for (const variantId of variantIds) {
        const inCart = variantQuantities[variantId] || 0;
        const inv = data.inventory[variantId] || data.inventory[String(variantId)];
        const inventoryQuantity = inv && (inv.inventoryQuantity != null ? inv.inventoryQuantity : inv.quantity);
        const available = inventoryQuantity != null && inventoryQuantity !== '' ? parseInt(inventoryQuantity, 10) : null;
        if (available != null && inCart > available) {
          return {
            excess: true,
            message:
              this.getTranslations().inventoryCartExceeded ||
              'Llevas más unidades de las que hay en tu sucursal seleccionada. Ajusta las cantidades o elige otra sucursal.',
          };
        }
      }
      return { excess: false };
    } catch (_) {
      return { excess: false };
    }
  }

  /**
   * Muestra u oculta el estado de carga en el botón de checkout (spinner + bloqueo).
   * Evita que el usuario haga click mientras se valida la sucursal/inventario.
   * @param {HTMLButtonElement} checkoutButton - Botón de checkout
   * @param {boolean} loading - true = mostrar spinner y bloquear, false = quitar spinner y restaurar contenido
   */
  setCheckoutButtonLoading(checkoutButton, loading) {
    if (!checkoutButton) return;
    if (loading) {
      if (!checkoutButton.hasAttribute('data-checkout-button-content')) {
        checkoutButton.setAttribute('data-checkout-button-content', checkoutButton.innerHTML);
      }
      checkoutButton.disabled = true;
      checkoutButton.setAttribute('aria-disabled', 'true');
      checkoutButton.classList.add('loading', 'is-disabled');
      let spinner = checkoutButton.querySelector('.loading__spinner');
      if (!spinner) {
        spinner = document.createElement('div');
        spinner.className = 'loading__spinner';
        spinner.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>';
        checkoutButton.appendChild(spinner);
      }
      spinner.classList.remove('hidden');
    } else {
      checkoutButton.classList.remove('loading');
      const spinner = checkoutButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
      const saved = checkoutButton.getAttribute('data-checkout-button-content');
      if (saved) {
        checkoutButton.innerHTML = saved;
        checkoutButton.removeAttribute('data-checkout-button-content');
      }
    }
  }

  /**
   * Actualiza el estado del botón de checkout (habilitado/deshabilitado)
   * Versión síncrona rápida (solo verifica localStorage)
   * Habilitado solo si ubicación válida Y no hay exceso duty-free.
   * @param {HTMLButtonElement} checkoutButton - Botón de checkout
   */
  updateCheckoutButtonState(checkoutButton) {
    if (!checkoutButton) return;

    const isValid = this.isPickupDataValid();
    const dutyFreeExcess = this.hasDutyFreeExcess();
    const canCheckout = isValid && !dutyFreeExcess;

    if (canCheckout) {
      checkoutButton.disabled = false;
      checkoutButton.removeAttribute('aria-disabled');
      checkoutButton.classList.remove('is-disabled');
      this.removeCheckoutError();
    } else {
      checkoutButton.disabled = true;
      checkoutButton.setAttribute('aria-disabled', 'true');
      checkoutButton.classList.add('is-disabled');
    }
  }

  /**
   * Actualiza el estado del botón de checkout de forma asíncrona
   * Verifica ubicación, duty-free e inventario por sucursal.
   * Muestra spinner y bloquea el botón durante la validación para evitar clicks prematuras.
   * @param {HTMLButtonElement} checkoutButton - Botón de checkout
   * @param {boolean} [showLoading=true] - Si true, muestra spinner mientras valida; si false, solo actualiza estado sin spinner
   */
  async updateCheckoutButtonStateAsync(checkoutButton, showLoading = true) {
    if (!checkoutButton) return;

    if (showLoading) {
      this.setCheckoutButtonLoading(checkoutButton, true);
    }

    try {
      const isValid = await this.isPickupDataValidAsync();
      const dutyFreeExcess = this.hasDutyFreeExcess();
      const inventoryExcess = await this.getCartInventoryExcess();
      const canCheckout = isValid && !dutyFreeExcess && !inventoryExcess.excess;

      if (canCheckout) {
        checkoutButton.disabled = false;
        checkoutButton.removeAttribute('aria-disabled');
        checkoutButton.classList.remove('is-disabled');
        this.removeCheckoutError();
      } else {
        checkoutButton.disabled = true;
        checkoutButton.setAttribute('aria-disabled', 'true');
        checkoutButton.classList.add('is-disabled');
        if (inventoryExcess.excess && inventoryExcess.message) {
          this.showCheckoutError(inventoryExcess.message);
        } else {
          this.removeCheckoutError();
        }
      }
    } finally {
      if (showLoading) {
        this.setCheckoutButtonLoading(checkoutButton, false);
      }
    }
  }

  /**
   * Muestra un mensaje de error cuando se intenta hacer checkout con datos inválidos o exceso de inventario
   * @param {string} [message] - Mensaje a mostrar; por defecto el de fecha/hora de recogida
   */
  showCheckoutError(message) {
    const defaultMessage =
      this.getTranslations().checkoutDatetimeInvalid ||
      'Por favor, verifica que la fecha y hora de recogida sean válidas (al menos 30 minutos después de ahora) antes de continuar al checkout.';
    const text = message != null && message !== '' ? message : defaultMessage;

    let errorElement = document.querySelector('.cart__checkout-error');
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'cart__checkout-error';
      errorElement.style.cssText = `
        background-color: #fee;
        color: #c33;
        padding: 12px 16px;
        border-radius: 4px;
        margin: 16px 0;
        border: 1px solid #fcc;
        font-size: 14px;
        line-height: 1.4;
      `;

      const checkoutButton = document.getElementById('checkout');
      const cartCtas = checkoutButton?.closest('.cart__ctas');
      if (cartCtas) {
        cartCtas.insertBefore(errorElement, checkoutButton);
      } else if (checkoutButton) {
        checkoutButton.parentElement.insertBefore(errorElement, checkoutButton);
      }
    }

    errorElement.textContent = text;
    errorElement.style.display = 'block';
    errorElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * Remueve el mensaje de error del checkout
   */
  removeCheckoutError() {
    const errorElement = document.querySelector('.cart__checkout-error');
    if (errorElement) {
      errorElement.style.display = 'none';
    }
  }

  /**
   * Sincroniza datos de localStorage al carrito si hay datos en localStorage pero no en cart attributes
   * Esto asegura que los datos seleccionados previamente se mantengan en el carrito
   */
  async syncLocalStorageToCart() {
    try {
      // Verificar si hay datos en localStorage
      const stored = localStorage.getItem('locationData');
      if (!stored) {
        return; // No hay datos en localStorage, no hay nada que sincronizar
      }

      const locationData = JSON.parse(stored);
      
      // Verificar si los datos son válidos
      const hasRequiredData =
        locationData &&
        locationData.country &&
        locationData.airport &&
        locationData.terminal &&
        locationData.date &&
        locationData.time &&
        locationData.location_id &&
        locationData.location_name;

      if (!hasRequiredData) {
        console.log('📝 [CART DEBUG] syncLocalStorageToCart - Datos en localStorage incompletos, no se sincronizan');
        return;
      }

      // Verificar si ya están en cart attributes
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const hasCartAttributes = 
        cart.attributes && 
        cart.attributes.pickup_location_id && 
        cart.attributes.pickup_location_name;

      // Si ya hay atributos en el carrito, no hacer nada
      if (hasCartAttributes) {
        console.log('✅ [CART DEBUG] syncLocalStorageToCart - Ya hay atributos en el carrito, no se necesita sincronizar');
        return;
      }

      // Hay datos válidos en localStorage pero no en cart attributes, sincronizar
      console.log('🔄 [CART DEBUG] syncLocalStorageToCart - Sincronizando datos de localStorage al carrito');
      console.log('📋 Datos a sincronizar:', locationData);

      // Usar locationSelectorPopup para actualizar los atributos (si está disponible)
      if (window.locationSelectorPopup && typeof window.locationSelectorPopup.updateCartAttributes === 'function') {
        await window.locationSelectorPopup.updateCartAttributes(locationData);
        console.log('✅ [CART DEBUG] syncLocalStorageToCart - Datos sincronizados correctamente');
        
        // Verificar que se guardaron
        await this.debugCartAttributes('🔄 [CART DEBUG] syncLocalStorageToCart - Verificación después de sincronizar');
      } else {
        console.warn('⚠️ [CART DEBUG] syncLocalStorageToCart - locationSelectorPopup no está disponible, intentando actualizar directamente');
        
        // Actualizar directamente usando la misma lógica que updateCartAttributes
        await this.updateCartAttributesDirectly(locationData);
      }
    } catch (error) {
      console.error('❌ [CART DEBUG] Error al sincronizar localStorage al carrito:', error);
    }
  }

  /**
   * Actualiza los cart attributes directamente (usado cuando locationSelectorPopup no está disponible)
   */
  async updateCartAttributesDirectly(locationData) {
    try {
      // Obtener atributos existentes
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const existingAttributes = cart.attributes || {};

      // Terminal para el atributo: siempre en español (no usar traducciones)
      const terminalText = this.getTerminalAttributeText(locationData.terminal);

      // Tipo de catálogo para extensiones y checkout: 'main' | 'electronics'
      const catalogType = (locationData.department === 'electronics') ? 'electronics' : 'main';

      // Preparar atributos de pickup
      const pickupAttributes = {
        catalog_type: String(catalogType),
        pickup_country: String(locationData.country || ''),
        pickup_airport: String(locationData.airport ? this.getAirportAttributeText() : ''),
        pickup_terminal: String(terminalText || ''),
        pickup_terminal_raw: String(locationData.terminal || ''),
        pickup_department: String(locationData.department || ''),
        pickup_date: String(locationData.date || ''),
        pickup_date_display: String(locationData.dateDisplay || locationData.date || ''),
        pickup_time: String(locationData.time || ''),
        pickup_time_display: String(locationData.timeDisplay || locationData.time || ''),
        pickup_location_id: String(locationData.location_id || ''),
        pickup_location_name: String(locationData.location_name || ''),
        pickup_timestamp: String(locationData.timestamp || new Date().toISOString()),
        branch_filter_selection: String(locationData.location_name || ''),
      };

      // Combinar atributos
      const allAttributes = {
        ...existingAttributes,
        ...pickupAttributes,
      };

      // Actualizar carrito
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: allAttributes,
        }),
      });

      if (response.ok) {
        const cartData = await response.json();
        console.log('✅ [CART DEBUG] updateCartAttributesDirectly - Atributos actualizados correctamente');
        console.log('📋 Atributos guardados:', cartData.attributes);
        return cartData;
      } else {
        console.error('❌ [CART DEBUG] Error al actualizar atributos:', response.statusText);
      }
    } catch (error) {
      console.error('❌ [CART DEBUG] Error en updateCartAttributesDirectly:', error);
    }
  }

  /**
   * Sincroniza todos los atributos de ubicación usando pickup_location_id como fuente de verdad
   * pickup_location_id es el más confiable porque es un ID único e inmutable de Shopify
   * Este método detecta y corrige inconsistencias automáticamente
   */
  async syncLocationAttributes() {
    // Solo sincronizar en la página del carrito
    if (!window.location.pathname.includes('/cart')) {
      return;
    }

    try {
      // Debug: Log antes de sincronizar
      await this.debugCartAttributes('🔄 [CART DEBUG] syncLocationAttributes - Antes de sincronizar');
      
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      if (!cart.attributes) return;

      const pickupLocationId = cart.attributes.pickup_location_id || '';
      const pickupLocationName = cart.attributes.pickup_location_name || '';
      const branchFilterSelection = cart.attributes.branch_filter_selection || '';
      const pickupDepartment = cart.attributes.pickup_department || '';
      const pickupTerminalRaw = cart.attributes.pickup_terminal_raw || '';

      // Si no hay location_id, no podemos sincronizar
      if (!pickupLocationId) {
        return;
      }

      // Mapeo de location_id a location_name (debe coincidir con getBranchConfig)
      const locationMapping = {
        'gid://shopify/Location/85681504448': {
          name: 'Tienda de Salida - El Salvador',
          department: 'general',
          terminal: 'terminal-1',
        },
        'gid://shopify/Location/86059614400': {
          name: 'Tienda de Llegada 2 - El Salvador',
          department: 'general',
          terminal: 'terminal-2',
        },
      };

      const expectedLocation = locationMapping[pickupLocationId];
      if (!expectedLocation) {
        console.warn('⚠️ pickup_location_id no reconocido:', pickupLocationId);
        return;
      }

      // Verificar si hay inconsistencias
      const needsSync =
        pickupLocationName !== expectedLocation.name ||
        branchFilterSelection !== expectedLocation.name;

      if (needsSync) {
        console.warn('⚠️ Inconsistencia detectada en atributos de ubicación');
        console.warn(`  pickup_location_id: "${pickupLocationId}"`);
        console.warn(`  pickup_location_name: "${pickupLocationName}" (esperado: "${expectedLocation.name}")`);
        console.warn(`  branch_filter_selection: "${branchFilterSelection}" (esperado: "${expectedLocation.name}")`);
        console.warn('  Sincronizando... (usando pickup_location_id como fuente de verdad)');

        // Usar pickup_location_id como fuente de verdad y corregir los demás
        const existingAttributes = { ...cart.attributes };
        const allAttributes = {
          ...existingAttributes,
          branch_filter_selection: expectedLocation.name,
          pickup_location_name: expectedLocation.name,
          // Asegurar que pickup_location_id esté correcto
          pickup_location_id: pickupLocationId,
        };

        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attributes: allAttributes,
          }),
        });

        if (updateResponse.ok) {
          console.log('✅ Atributos sincronizados correctamente');
          console.log(`  pickup_location_name actualizado a: "${expectedLocation.name}"`);
          console.log(`  branch_filter_selection actualizado a: "${expectedLocation.name}"`);
          
          // Debug: Log después de sincronizar
          await this.debugCartAttributes('🔄 [CART DEBUG] syncLocationAttributes - Después de sincronizar');
        } else {
          console.error('❌ Error al sincronizar atributos:', updateResponse.statusText);
        }
      }
    } catch (error) {
      console.error('Error al sincronizar atributos de ubicación:', error);
    }
  }
}

// Crear instancia global para que pueda ser accedida desde otros scripts
let cartPickupDataInstance = null;

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    cartPickupDataInstance = new CartPickupData();
    window.cartPickupData = cartPickupDataInstance;
  });
} else {
  cartPickupDataInstance = new CartPickupData();
  window.cartPickupData = cartPickupDataInstance;
}
